/**
 * @fileoverview Analytics Service
 * Service for managing Google Analytics 4 integration with consent awareness
 */

import { GA4_TRACKING_ID } from '../../types/analytics.types';
import ConsentManager from '../consent/ConsentManager';

/**
 * AnalyticsService - Singleton service for Google Analytics 4
 */
class AnalyticsService {
  constructor() {
    if (AnalyticsService.instance) {
      return AnalyticsService.instance;
    }
    
    this.trackingId = null;
    this.initialized = false;
    this.scriptLoaded = false;
    AnalyticsService.instance = this;
  }

  /**
   * Initialize Google Analytics 4
   * @param {string} trackingId - GA4 tracking ID (default: G-RBK22H8FL1)
   */
  initialize(trackingId = GA4_TRACKING_ID) {
    if (this.initialized) return;
    
    this.trackingId = trackingId;
    
    // Check if user has granted analytics consent
    if (ConsentManager.hasAnalyticsConsent()) {
      this.loadScript();
    }
    
    // Listen for consent changes
    ConsentManager.onConsentChange((preferences) => {
      this.updateConsent(preferences.analytics);
    });
    
    this.initialized = true;
  }

  /**
   * Load Google Analytics script
   */
  loadScript() {
    if (this.scriptLoaded) return;
    if (typeof window === 'undefined') return;
    
    // Check Do Not Track
    if (this.isDNTEnabled()) {
      console.log('Do Not Track is enabled, skipping analytics');
      return;
    }

    try {
      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      
      // Set timestamp
      window.gtag('js', new Date());
      
      // Configure GA4
      window.gtag('config', this.trackingId, {
        'anonymize_ip': true,
        'cookie_flags': 'SameSite=None;Secure'
      });
      
      // Load GA4 script
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.trackingId}`;
      script.async = true;
      
      script.onload = () => {
        this.scriptLoaded = true;
      };
      
      script.onerror = () => {
        console.error('Failed to load Google Analytics script');
      };
      
      document.head.appendChild(script);
    } catch (error) {
      console.error('Error loading Google Analytics:', error);
    }
  }

  /**
   * Track page view
   * @param {string} path - Page path
   * @param {string} title - Page title
   */
  trackPageView(path, title) {
    if (!this.isLoaded() || !ConsentManager.hasAnalyticsConsent()) return;
    
    try {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title
      });
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }

  /**
   * Track custom event
   * @param {string} eventName - Event name
   * @param {Record<string, any>} parameters - Event parameters
   */
  trackEvent(eventName, parameters = {}) {
    if (!this.isLoaded() || !ConsentManager.hasAnalyticsConsent()) return;
    
    try {
      window.gtag('event', eventName, parameters);
    } catch (error) {
      console.error(`Error tracking event ${eventName}:`, error);
    }
  }

  /**
   * Track button click
   * @param {string} buttonName - Button name or label
   * @param {string} location - Location on page
   */
  trackButtonClick(buttonName, location) {
    this.trackEvent('button_click', {
      button_name: buttonName,
      location: location,
      event_category: 'engagement'
    });
  }

  /**
   * Track outbound link click
   * @param {string} url - Outbound link URL
   */
  trackOutboundLink(url) {
    this.trackEvent('outbound_link', {
      link_url: url,
      event_category: 'engagement'
    });
  }

  /**
   * Track scroll depth
   * @param {number} percentage - Scroll depth percentage
   */
  trackScrollDepth(percentage) {
    this.trackEvent('scroll_depth', {
      percentage: percentage,
      event_category: 'engagement'
    });
  }

  /**
   * Track calculator usage
   * @param {string} calculatorType - Type of calculator
   */
  trackCalculatorUsage(calculatorType) {
    this.trackEvent('calculator_used', {
      calculator_type: calculatorType,
      event_category: 'engagement',
      event_label: 'financial_tools'
    });
  }

  /**
   * Track stock selection
   * @param {string} symbol - Stock symbol
   * @param {string} name - Stock name
   */
  trackStockSelection(symbol, name) {
    this.trackEvent('stock_selected', {
      stock_symbol: symbol,
      stock_name: name,
      event_category: 'engagement'
    });
  }

  /**
   * Update consent status
   * @param {boolean} hasConsent - Whether analytics consent is granted
   */
  updateConsent(hasConsent) {
    if (hasConsent && !this.scriptLoaded) {
      // Load script if consent granted and not yet loaded
      this.loadScript();
    } else if (hasConsent && this.scriptLoaded) {
      // Send consent update event when consent is granted
      this.trackEvent('consent_update', {
        consent_type: 'analytics',
        consent_status: 'granted'
      });
    } else if (!hasConsent) {
      // Send consent update event when consent is revoked (if script is loaded)
      if (this.scriptLoaded) {
        this.trackEvent('consent_update', {
          consent_type: 'analytics',
          consent_status: 'denied'
        });
      }
      // Clear analytics cookies
      this.clearAnalyticsCookies();
    }
  }

  /**
   * Clear analytics cookies
   */
  clearAnalyticsCookies() {
    const cookies = document.cookie.split(';');
    
    cookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      
      // Clear GA cookies
      if (cookieName.startsWith('_ga') || cookieName.startsWith('_gid')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });
  }

  /**
   * Check if Do Not Track is enabled
   * @returns {boolean}
   */
  isDNTEnabled() {
    if (typeof window === 'undefined') return false;
    
    return (
      navigator.doNotTrack === '1' ||
      window.doNotTrack === '1' ||
      navigator.msDoNotTrack === '1'
    );
  }

  /**
   * Check if analytics is loaded
   * @returns {boolean}
   */
  isLoaded() {
    return this.scriptLoaded && typeof window !== 'undefined' && typeof window.gtag === 'function';
  }
}

// Export singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;
